import { addCustomRumError } from "../../Datadog/utils/rumActions";

/**
 * Which stage of Skia's web surface setup failed.
 *
 * - `contextCreation`, CanvasKit could not get a WebGL context at all.
 *   `MakeWebGLCanvasSurface` throws the bare string
 *   `"failed to create webgl context: err 0"` (canvaskit.js).
 * - `surfaceCreation`, a context existed but the Skia surface could not be
 *   built on it. `SkiaBaseWebView.onLayoutEvent` throws `"Could not create
 *   surface"`.
 */
export type SkiaSurfaceFailure = "contextCreation" | "surfaceCreation";

const FAILURE_PATTERNS: [RegExp, SkiaSurfaceFailure][] = [
  [/failed to create webgl context/i, "contextCreation"],
  [/could not create surface/i, "surfaceCreation"],
];

/**
 * CanvasKit throws a bare STRING, not an Error, so the message can arrive as
 * either. Read both shapes defensively.
 */
const messageOf = (error: unknown): string | undefined => {
  if (typeof error === "string") return error;

  if (typeof error === "object" && error !== null) {
    const { message } = error as { message?: unknown };
    if (typeof message === "string") return message;
  }

  return undefined;
};

export const classifySkiaSurfaceFailure = (
  error: unknown,
): SkiaSurfaceFailure | undefined => {
  const message = messageOf(error);
  if (!message) return undefined;

  return FAILURE_PATTERNS.find(([pattern]) => pattern.test(message))?.[1];
};

/**
 * Reports Skia web surface failures to RUM.
 *
 * This has to be a global listener rather than a React error boundary. Skia
 * creates its surface inside `onLayoutEvent`, which a ResizeObserver dispatches
 * through `setTimeout(…, 0)`, so the throw is outside React's call stack
 * entirely, it lands on `window.onerror`. That is why these show up in RUM as
 * `handling: "unhandled"` today, and why the signature pad's own error boundary
 * cannot catch them (DHT-4858).
 *
 * Reporting does not fix anything. It converts an anonymous unhandled error into
 * an attributed one, so the next occurrence says which stage failed and on which
 * screen, and so the count can be tracked as context pressure is reduced.
 *
 * Mirrors `installChunkErrorReloadHandler` in Common/Updates/utils/chunkReload,
 * and is installed next to it from index.web.js.
 */
export const installSkiaSurfaceErrorReporter = (): void => {
  if (typeof window === "undefined") return;

  const report = (error: unknown) => {
    const failure = classifySkiaSurfaceFailure(error);
    if (!failure) return;

    addCustomRumError("[Skia] Web surface unavailable", {
      failure,
      // The screen matters: the leading explanation is too many concurrently
      // mounted Skia canvases, so knowing where it fired narrows which ones.
      path: window.location?.pathname,
      errorMessage: messageOf(error) ?? "",
    });
  };

  // Deliberately passive listeners: this only observes. Swallowing the error
  // would hide it from Datadog's own unhandled-error capture, and there is
  // nothing useful to recover here anyway.
  window.addEventListener("error", event =>
    report(event.error ?? event.message),
  );
  window.addEventListener("unhandledrejection", event => report(event.reason));
};
